// features/dashboard/dashboardSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchDashboardDataAPI,
  fetchLockoutDevicesAPI,
  fetchInvoicesAPI,
  checkPaymentStatusAPI,
  createPaymentIntentAPI,
  cancelPaymentAPI,
  processCashPaymentAPI
} from './dashboardAPI';

// Async Thunks
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchDashboardData',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await fetchDashboardDataAPI(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLockoutDevices = createAsyncThunk(
  'dashboard/fetchLockoutDevices',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await fetchLockoutDevicesAPI(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchInvoices = createAsyncThunk(
  'dashboard/fetchInvoices',
  async (dealerId, { rejectWithValue }) => {
    try {
      const response = await fetchInvoicesAPI(dealerId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const checkPaymentStatus = createAsyncThunk(
  'dashboard/checkPaymentStatus',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await checkPaymentStatusAPI(customerId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPaymentIntent = createAsyncThunk(
  'dashboard/createPaymentIntent',
  async ({ dealerId, amount }, { rejectWithValue }) => {
    try {
      const response = await createPaymentIntentAPI(dealerId, amount);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelPayment = createAsyncThunk(
  'dashboard/cancelPayment',
  async (paymentIntentId, { rejectWithValue }) => {
    try {
      const response = await cancelPaymentAPI(paymentIntentId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const processCashPayment = createAsyncThunk(
  'dashboard/processCashPayment',
  async ({ dealerId, adminPassword, paymentIntentId, userEmail }, { rejectWithValue }) => {
    try {
      const response = await processCashPaymentAPI(dealerId, adminPassword, paymentIntentId, userEmail);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  // Dashboard Data
  metrics: {
    totalDevices: 0,
    availableDevices: 0,
    currentRevenue: 0,
    totalRevenue: 0,
    newClients: 0,
    totalClients: 0,
    newInstallations: 0,
    removalsThisMonth: 0,
  },
  charts: {
    salesData: [],
    salesLabels: [],
    salesValues: [],
  },
  tables: {
    calibrations: [],
    lockouts: [],
  },
  
  // Payment State
  paymentStatus: {
    hasAccess: false,
    isPaymentDue: false,
    message: '',
    header: '',
  },
  invoices: [],
  paymentIntent: {
    clientSecret: null,
    intentId: null,
    amount: 0,
  },
  
  // UI State
  loading: false,
  error: null,
  paymentDialogOpen: false,
  cashDialogOpen: false,
  activeTimer: null,
  timeLeft: 0,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setPaymentDialogOpen: (state, action) => {
      state.paymentDialogOpen = action.payload;
    },
    setCashDialogOpen: (state, action) => {
      state.cashDialogOpen = action.payload;
    },
    setActiveTimer: (state, action) => {
      state.activeTimer = action.payload;
    },
    setTimeLeft: (state, action) => {
      state.timeLeft = action.payload;
    },
    decrementTimeLeft: (state) => {
      state.timeLeft = state.timeLeft > 0 ? state.timeLeft - 1 : 0;
    },
    resetPaymentState: (state) => {
      state.paymentIntent = initialState.paymentIntent;
      state.paymentDialogOpen = false;
      state.cashDialogOpen = false;
      state.timeLeft = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboard Data
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.metrics = action.payload.metrics;
        state.charts = action.payload.charts;
        state.tables.calibrations = action.payload.calibrations;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Lockout Devices
      .addCase(fetchLockoutDevices.fulfilled, (state, action) => {
        state.tables.lockouts = action.payload;
      })
      
      // Check Payment Status
      .addCase(checkPaymentStatus.fulfilled, (state, action) => {
        state.paymentStatus = action.payload;
      })
      
      // Fetch Invoices
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.invoices = action.payload;
      })
      
      // Create Payment Intent
      .addCase(createPaymentIntent.fulfilled, (state, action) => {
        state.paymentIntent = action.payload;
        state.paymentDialogOpen = true;
      })
      
      // Cancel Payment
      .addCase(cancelPayment.fulfilled, (state) => {
        state.paymentIntent = initialState.paymentIntent;
        state.paymentDialogOpen = false;
      })
      
      // Process Cash Payment
      .addCase(processCashPayment.fulfilled, (state) => {
        state.cashDialogOpen = false;
        state.paymentStatus.hasAccess = true;
        state.paymentStatus.isPaymentDue = false;
      });
  },
});

export const {
  setPaymentDialogOpen,
  setCashDialogOpen,
  setActiveTimer,
  setTimeLeft,
  decrementTimeLeft,
  resetPaymentState,
  clearError,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;